import { ArrowBackOutlined } from "@aircall/icons";
import { Flex, Menu, MenuItem, Spacer, Typography } from "@aircall/tractor";
import type { ComponentType } from "react";
import { useSearchParams } from "react-router-dom";

import { PlaygroundAccordion } from "../PlaygroundAccordion/PlaygroundAccordion";
import { PlaygroundAnimatedScreen } from "../PlaygroundAnimatedScreen/PlaygroundAnimatedScreen";
import { PlaygroundButtonLink } from "../PlaygroundButtonLink/PlaygroundButtonLink";
import { PlaygroundCsatDriver } from "../PlaygroundCsatDriver/PlaygroundCsatDriver";
import { PlaygroundCsatScore } from "../PlaygroundCsatScore/PlaygroundCsatScore";
import { PlaygroundCsatScoreRow } from "../PlaygroundCsatScoreRow/PlaygroundCsatScoreRow";
import { PlaygroundDynamicLoader } from "../PlaygroundDynamicLoader/PlaygroundDynamicLoader";
import { PlaygroundEvaluationField } from "../PlaygroundEvaluationField/PlaygroundEvaluationField";
import { PlaygroundEvaluationQuestionSummaryRating } from "../PlaygroundEvaluationQuestionSummaryRating/PlaygroundEvaluationQuestionSummaryRating";
import { PlaygroundFeatureBanner } from "../PlaygroundFeatureBanner/PlaygroundFeatureBanner";
import { PlaygroundFeedbacks } from "../PlaygroundFeedbacks/PlaygroundFeedbacks";
import { PlaygroundFloatingButton } from "../PlaygroundFloatingButton/PlaygroundFloatingButton";
import { PlaygroundHelpButton } from "../PlaygroundHelpButton/PlaygroundHelpButton";
import { PlaygroundInputChoices } from "../PlaygroundInputChoices/PlaygroundInputChoices";
import { PlaygroundPercentBadge } from "../PlaygroundPercentBadge/PlaygroundPercentBadge";
import { PlaygroundRequireAgentLicense } from "../PlaygroundRequireAgentLicense/PlaygroundRequireAgentLicense";
import { PlaygroundReviewEvaluationField } from "../PlaygroundReviewEvaluationField/PlaygroundReviewEvaluationField";
import { PlaygroundReviewHeader } from "../PlaygroundReviewHeader/PlaygroundReviewHeader";
import { PlaygroundReviewNotes } from "../PlaygroundReviewNotes/PlaygroundReviewNotes";
import { PlaygroundReviewScorecards } from "../PlaygroundReviewScorecards/PlaygroundReviewScorecards";
import { PlaygroundReviewToast } from "../PlaygroundReviewToast/PlaygroundReviewToast";
import { PlaygroundSectionCallEvaluation } from "../PlaygroundSectionCallEvaluation/PlaygroundSectionCallEvaluation";
import { PlaygroundSectionCallType } from "../PlaygroundSectionCallType/PlaygroundSectionCallType";
import { PlaygroundSectionCallUuid } from "../PlaygroundSectionCallUuid/PlaygroundSectionCallUuid";
import { PlaygroundSectionCsat } from "../PlaygroundSectionCsat/PlaygroundSectionCsat";
import { PlaygroundSectionCustomSummary } from "../PlaygroundSectionCustomSummary/PlaygroundSectionCustomSummary";
import { PlaygroundSectionDisconnectedBy } from "../PlaygroundSectionDisconnectedBy/PlaygroundSectionDisconnectedBy";
import { PlaygroundSectionKeyTopics } from "../PlaygroundSectionKeyTopics/PlaygroundSectionKeyTopics";
import { PlaygroundSectionNextActions } from "../PlaygroundSectionNextActions/PlaygroundSectionNextActions";
import { PlaygroundSectionNoInformation } from "../PlaygroundSectionNoInformation/PlaygroundSectionNoInformation";
import { PlaygroundSectionNotes } from "../PlaygroundSectionNotes/PlaygroundSectionNotes";
import { PlaygroundSectionNumber } from "../PlaygroundSectionNumber/PlaygroundSectionNumber";
import { PlaygroundSectionParticipants } from "../PlaygroundSectionParticipants/PlaygroundSectionParticipants";
import { PlaygroundSectionPlaybook } from "../PlaygroundSectionPlaybook/PlaygroundSectionPlaybook";
import { PlaygroundSectionSentiment } from "../PlaygroundSectionSentiment/PlaygroundSectionSentiment";
import { PlaygroundSectionSummary } from "../PlaygroundSectionSummary/PlaygroundSectionSummary";
import { PlaygroundSectionTags } from "../PlaygroundSectionTags/PlaygroundSectionTags";
import { PlaygroundSectionTalkRatio } from "../PlaygroundSectionTalkRatio/PlaygroundSectionTalkRatio";
import { PlaygroundSectionTiming } from "../PlaygroundSectionTiming/PlaygroundSectionTiming";
import { PlaygroundSeparator } from "../PlaygroundSeparator/PlaygroundSeparator";
import { PlaygroundShareLink } from "../PlaygroundShareLink/PlaygroundShareLink";
import { PlaygroundSlider } from "../PlaygroundSlider/PlaygroundSlider";
import { PlaygroundTagDisplay } from "../PlaygroundTagDisplay/PlaygroundTagDisplay";
import { PlaygroundTimelineModePicker } from "../PlaygroundTimelineModePicker/PlaygroundTimelineModePicker";
import { PlaygroundToggleSpeakers } from "../PlaygroundToggleSpeakers/PlaygroundToggleSpeakers";
import { PlaygroundTranscriptionEmptyStates } from "../PlaygroundTranscriptionEmptyStates/PlaygroundTranscriptionEmptyStates";
import { PlaygroundVirtualAgentTag } from "../PlaygroundVirtualAgentTag/PlaygroundVirtualAgentTag";

const COMPONENTS: Record<string, ComponentType> = {
  Accordion: PlaygroundAccordion,
  PercentBadge: PlaygroundPercentBadge,
  CsatScore: PlaygroundCsatScore,
  CsatScoreRow: PlaygroundCsatScoreRow,
  CsatDriver: PlaygroundCsatDriver,
  EvaluationField: PlaygroundEvaluationField,
  Feedbacks: PlaygroundFeedbacks,
  InputChoices: PlaygroundInputChoices,
  ReviewNotesAvatar: PlaygroundReviewNotes,
  ReviewScorecardSelector: PlaygroundReviewScorecards,
  ReviewEvaluationField: PlaygroundReviewEvaluationField,
  ReviewCreateFormHeader: PlaygroundReviewHeader,
  ReviewPublishToast: PlaygroundReviewToast,
  VirtualAgentTag: PlaygroundVirtualAgentTag,
  EvaluationQuestionSummaryRating: PlaygroundEvaluationQuestionSummaryRating,
  Slider: PlaygroundSlider,
  TagDisplay: PlaygroundTagDisplay,
  SectionCallEvaluation: PlaygroundSectionCallEvaluation,
  SectionCallType: PlaygroundSectionCallType,
  SectionCallUuid: PlaygroundSectionCallUuid,
  SectionCsat: PlaygroundSectionCsat,
  SectionCustomSummary: PlaygroundSectionCustomSummary,
  SectionDisconnectedBy: PlaygroundSectionDisconnectedBy,
  SectionKeyTopics: PlaygroundSectionKeyTopics,
  SectionNextActions: PlaygroundSectionNextActions,
  SectionNotes: PlaygroundSectionNotes,
  SectionNumber: PlaygroundSectionNumber,
  SectionParticipants: PlaygroundSectionParticipants,
  SectionPlaybook: PlaygroundSectionPlaybook,
  SectionSentiment: PlaygroundSectionSentiment,
  SectionTags: PlaygroundSectionTags,
  SectionTalkRatio: PlaygroundSectionTalkRatio,
  SectionTiming: PlaygroundSectionTiming,
  AnimatedScreen: PlaygroundAnimatedScreen,
  DynamicLoader: PlaygroundDynamicLoader,
  FloatingButton: PlaygroundFloatingButton,
  HelpButton: PlaygroundHelpButton,
  Separator: PlaygroundSeparator,
  ShareLink: PlaygroundShareLink,
  ButtonLink: PlaygroundButtonLink,
  FeatureBanner: PlaygroundFeatureBanner,
  TimelineModePicker: PlaygroundTimelineModePicker,
  RequireAgentLicense: PlaygroundRequireAgentLicense,
  ToggleSpeakers: PlaygroundToggleSpeakers,
  SectionSummary: PlaygroundSectionSummary,
  TranscriptionEmptyStates: PlaygroundTranscriptionEmptyStates,
  NoInformation: PlaygroundSectionNoInformation,
};

export function Playground() {
  const [searchParams, setSearchParams] = useSearchParams();
  const componentName = searchParams.get("component");
  const Component = componentName ? COMPONENTS[componentName] : null;

  if (!Component) {
    const componentKeys = Object.keys(COMPONENTS);
    return (
      <Flex
        background="white"
        p="s"
        flexDirection="column"
        gap="s"
        h="100%"
        overflow="auto"
      >
        {componentKeys.length > 0 ? (
          <Menu>
            {componentKeys.map((name) => (
              <MenuItem
                key={name}
                onClick={() => setSearchParams({ component: name })}
              >
                {name}
              </MenuItem>
            ))}
          </Menu>
        ) : (
          <Typography>No components registered yet.</Typography>
        )}
      </Flex>
    );
  }

  return (
    <Flex
      flexDirection="column"
      background="white"
      p="s"
      gap="m"
      h="100%"
      overflow="auto"
    >
      <Spacer
        gap="xxs"
        alignItems="center"
        cursor="pointer"
        onClick={() => setSearchParams({})}
        data-test="playground-back"
      >
        <ArrowBackOutlined size={16} />
        <Typography variant="bodyMediumS">Back to components</Typography>
      </Spacer>
      <Component />
    </Flex>
  );
}
